// Reglas de pantalla de la reanudación. El QUÉ falta lo decide el silo; aquí solo se traduce a slug
// de ruta y a texto para el vendedor.
import { ACCOUNT_OPENING_RESUME_READY, hasAccountOpeningMark } from './accountOpening';
import { accountAlreadyOpen } from './accountWait';
import { ACCOUNT_OPENING_STEPS, slugForStep } from './steps';
import type { ResumeReason, WizardSession, WizardStepId, WizardStepSlug } from './types';

/** ¿El teléfono se probó alguna vez en esta venta? Es el progreso real, ajeno al paso del cursor. */
export function hasProvenPhone(
    session: Pick<WizardSession, 'otpVerified' | 'otpSkipped'>,
): boolean {
    return session.otpVerified || session.otpSkipped;
}

/**
 * ¿El código ya se verificó para esta pantalla? Solo cuenta el verificado: un `otpSkipped` heredado
 * no deja avanzar. Una reanudación pendiente lo vuelve a pedir.
 */
export function otpSettled(
    session: Pick<WizardSession, 'otpVerified'> & Partial<Pick<WizardSession, 'resumeOtpRequired'>>,
): boolean {
    if (session.resumeOtpRequired) return false;
    return session.otpVerified;
}

/**
 * Slug al que sale el paso 01 tras el OTP: la pantalla guardada, luego el destino del silo, luego el
 * KYC. Un destino que resuelve al propio 01 tampoco devuelve al código recién tecleado.
 */
export function stepAfterOtp(
    session: Partial<
        Pick<
            WizardSession,
            | 'resumeTarget'
            | 'resumeSlug'
            | 'accountOpening'
            | 'currentStep'
            | 'accountStatus'
            | 'state'
        >
    >,
): WizardStepSlug {
    if (hasAccountOpeningMark(session)) return accountOpeningStepAfterOtp(session);
    const slug = session.resumeSlug ?? slugForStep(session.resumeTarget?.step ?? '02', session);
    return slug === 'otp' ? 'kyc' : slug;
}

/** Cursores de la apertura que ya pasaron Referidos: S11 (guardado u omitido) y S10. */
const PAST_REFERRALS_STEPS = new Set<WizardStepId>(['11', '10']);

/**
 * Destino de una apertura con el criterio del silo (`resolveAccountOpeningStep`): sin cuenta,
 * Verificación; con cuenta, App y tarjeta hasta que el cursor pasa Referidos.
 */
export function accountOpeningResumeSlug(
    session: Partial<Pick<WizardSession, 'currentStep' | 'accountStatus'>>,
): WizardStepSlug {
    if (!accountAlreadyOpen(session.accountStatus)) return 'kyc';
    return session.currentStep && PAST_REFERRALS_STEPS.has(session.currentStep)
        ? 'welcome'
        : 'delivery';
}

/** Apertura cerrada con el bono ya resuelto: el silo solo mueve el cursor a S10 al acreditarlo u omitirlo. */
export function isAccountOpeningFinished(
    session: Partial<Pick<WizardSession, 'state' | 'currentStep'>>,
): boolean {
    return session.state === 'COMPLETED' && session.currentStep === '10';
}

/** La pantalla o el destino del silo si es un paso de la apertura más allá del 01; si no, el espejo. */
function accountOpeningStepAfterOtp(session: Parameters<typeof stepAfterOtp>[0]): WizardStepSlug {
    if (isAccountOpeningFinished(session)) return 'summary';
    const siloStep = session.resumeSlug
        ? ACCOUNT_OPENING_STEPS.find((s) => s.slug === session.resumeSlug)
        : ACCOUNT_OPENING_STEPS.find((s) => s.id === session.resumeTarget?.step);
    if (siloStep && siloStep.slug !== 'otp') return siloStep.slug;
    return accountOpeningResumeSlug(session);
}

const REASON_TEXT: Record<ResumeReason, string> = {
    ACCOUNT_READY: 'Retomas la venta donde se quedó.',
    KYC_PENDING: 'Falta verificar la identidad del cliente.',
    ADDRESS_PENDING: 'Falta el comprobante de domicilio.',
    PROFILE_PENDING: 'Faltan la ocupación y el ingreso del cliente.',
    ACCOUNT_PENDING: 'Falta que abra la cuenta PagoConFiado.',
};

/** Qué le falta a la venta, en una línea. `undefined` para un motivo que este front no conoce. */
export function resumeReasonText(
    reason: ResumeReason | undefined,
    accountOpening = false,
): string | undefined {
    if (accountOpening && reason === 'ACCOUNT_READY') return ACCOUNT_OPENING_RESUME_READY;
    return reason ? REASON_TEXT[reason] : undefined;
}
